/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Simplified Test Bypass Mechanism - 2025
 *
 * A simplified version that avoids Next.js headers import issues
 * and focuses on basic test bypass functionality.
 */

import { logger } from "@/lib/utils/logger";
import { useMemo } from "react";

// Test bypass configuration
export interface TestBypassConfig {
  enabled: boolean;
  allowedEnvironments: string[];
  userIdOverride?: string;
  organizationIdOverride?: string;
  roleOverride?: string;
}

// Test user configuration
export interface TestUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId: string;
}

// Default test users type
export interface DefaultTestUsers {
  owner: TestUser;
  admin: TestUser;
  member: TestUser;
  [key: string]: TestUser;
}

// Default test users — UUIDs stripped from production bundles via dead-code elimination
const EMPTY_USER: TestUser = {
  id: "",
  email: "",
  firstName: "",
  lastName: "",
  role: "",
  organizationId: "",
};
export const DEFAULT_TEST_USERS: DefaultTestUsers =
  process.env.NODE_ENV === "production"
    ? { owner: EMPTY_USER, admin: EMPTY_USER, member: EMPTY_USER }
    : {
        owner: {
          id: "660e8400-e29b-41d4-a716-446655440001",
          email: "sarah.johnson@evergreenlandscapes.com",
          firstName: "Sarah",
          lastName: "Johnson",
          role: "OWNER",
          organizationId: "550e8400-e29b-41d4-a716-446655440001",
        },
        admin: {
          id: "660e8400-e29b-41d4-a716-446655440002",
          email: "mike.chen@evergreenlandscapes.com",
          firstName: "Mike",
          lastName: "Chen",
          role: "ADMIN",
          organizationId: "550e8400-e29b-41d4-a716-446655440001",
        },
        member: {
          id: "660e8400-e29b-41d4-a716-446655440003",
          email: "jessica.rodriguez@evergreenlandscapes.com",
          firstName: "Jessica",
          lastName: "Rodriguez",
          role: "ESTIMATOR",
          organizationId: "550e8400-e29b-41d4-a716-446655440001",
        },
      };

// Test bypass detection result
export interface TestBypassResult {
  isActive: boolean;
  source: "disabled" | "environment" | "headers" | "url" | "forced";
  user?: TestUser;
  organizationId?: string;
  config: TestBypassConfig;
  securityWarnings: string[];
}

/**
 * Get test bypass configuration with security validation
 */
export function getTestBypassConfig(): TestBypassConfig {
  const environment = process.env.NODE_ENV || "production";
  const allowTestBypass = process.env.ALLOW_TEST_BYPASS === "true";
  const forceTestMode = process.env.E2E_TEST_MODE === "true";

  // Security check: Never allow test bypass in production
  if (environment === "production" && !process.env.ALLOW_PRODUCTION_TESTING) {
    return {
      enabled: false,
      allowedEnvironments: [],
    };
  }

  // Enable test bypass in development and test environments
  const isDevelopmentOrTest =
    environment === "development" || environment === "test";

  return {
    enabled: allowTestBypass || forceTestMode || isDevelopmentOrTest,
    allowedEnvironments: ["development", "test", "preview"],
    userIdOverride: process.env.TEST_USER_ID,
    organizationIdOverride: process.env.TEST_ORGANIZATION_ID,
    roleOverride: process.env.TEST_USER_ROLE,
  };
}

/**
 * Simple environment-based test bypass detection
 * Works in both server and client contexts
 */
export function detectTestBypass(): TestBypassResult {
  const config = getTestBypassConfig();
  const securityWarnings: string[] = [];

  if (!config.enabled) {
    return {
      isActive: false,
      source: "disabled",
      config,
      securityWarnings,
    };
  }

  // Check for forced test mode (CI/E2E environments)
  if (process.env.E2E_TEST_MODE === "true") {
    const userId = config.userIdOverride ?? DEFAULT_TEST_USERS.owner.id;
    const orgId =
      config.organizationIdOverride ?? DEFAULT_TEST_USERS.owner.organizationId;

    logger.info("🧪 [TEST MODE] Test bypass active via environment", {
      userId,
      orgId,
      source: "environment",
    });

    return {
      isActive: true,
      source: "environment",
      user: DEFAULT_TEST_USERS.owner,
      organizationId: orgId,
      config,
      securityWarnings,
    };
  }

  // Check for test bypass environment variables
  if (process.env.ALLOW_TEST_BYPASS === "true") {
    const userId = config.userIdOverride ?? DEFAULT_TEST_USERS.owner.id;
    const orgId =
      config.organizationIdOverride ?? DEFAULT_TEST_USERS.owner.organizationId;

    logger.info("🧪 [TEST MODE] Test bypass active via config", {
      userId,
      orgId,
      source: "environment",
    });

    return {
      isActive: true,
      source: "environment",
      user: DEFAULT_TEST_USERS.owner,
      organizationId: orgId,
      config,
      securityWarnings,
    };
  }

  return {
    isActive: false,
    source: "disabled",
    config,
    securityWarnings,
  };
}

/**
 * Client-side test bypass detection (React components)
 */
export function detectClientSideTestBypass(): TestBypassResult {
  const config = getTestBypassConfig();
  const securityWarnings: string[] = [];

  if (!config.enabled) {
    return {
      isActive: false,
      source: "disabled",
      config,
      securityWarnings,
    };
  }

  if (typeof window === "undefined") {
    return {
      isActive: false,
      source: "disabled",
      config,
      securityWarnings: ["Window not available on server"],
    };
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const hasTestBypass =
      urlParams.has("test-bypass") && urlParams.get("test-bypass") === "true";

    if (hasTestBypass) {
      const userId =
        urlParams.get("test-user-id") ??
        config.userIdOverride ??
        DEFAULT_TEST_USERS.owner.id;
      const orgId =
        urlParams.get("test-org-id") ??
        config.organizationIdOverride ??
        DEFAULT_TEST_USERS.owner.organizationId;
      const role =
        urlParams.get("test-user-role") ?? config.roleOverride ?? "OWNER";

      const user = Object.values(DEFAULT_TEST_USERS).find(
        (u) => u.id === userId,
      ) ?? {
        id: userId,
        email: "test@example.com",
        firstName: "Test",
        lastName: "User",
        role,
        organizationId: orgId,
      };

      logger.info("🧪 [TEST MODE] Client-side test bypass active via URL", {
        userId,
        orgId,
        role,
        source: "url",
        url: window.location.href,
      });

      return {
        isActive: true,
        source: "url",
        user,
        organizationId: orgId,
        config,
        securityWarnings,
      };
    }

    // Check for test bypass globals (from Playwright/E2E)
    const hasTestGlobals = window.__TEST_BYPASS_ENABLED;
    if (hasTestGlobals) {
      const userId = window.__TEST_USER_ID ?? DEFAULT_TEST_USERS.owner.id;
      const orgId =
        window.__TEST_ORG_ID ?? DEFAULT_TEST_USERS.owner.organizationId;
      const roleFromGlobal =
        window.__TEST_USER_ROLE ?? config.roleOverride ?? "OWNER";

      // Build user object with role from globals
      const user = Object.values(DEFAULT_TEST_USERS).find(
        (u) => u.id === userId,
      ) ?? {
        ...DEFAULT_TEST_USERS.owner,
        id: userId,
        organizationId: orgId,
        role: roleFromGlobal,
      };

      // Override role if provided in globals
      if (window.__TEST_USER_ROLE) {
        user.role = roleFromGlobal;
      }

      logger.info("🧪 [TEST MODE] Client-side test bypass active via globals", {
        userId,
        orgId,
        role: user.role,
        source: "environment",
      });

      return {
        isActive: true,
        source: "environment",
        user,
        organizationId: orgId,
        config,
        securityWarnings,
      };
    }
  } catch (error) {
    logger.error("[TEST MODE] Error detecting client-side test bypass:", error);
    securityWarnings.push("Failed to parse URL for test bypass detection");
  }

  return {
    isActive: false,
    source: "disabled",
    config,
    securityWarnings,
  };
}

/**
 * Unified test bypass hook for React components
 * Memoized to avoid calling expensive detection on every render
 */
export function useTestBypass(): {
  isActive: boolean;
  user: TestUser | null;
  organizationId: string | null;
  source?: string;
  warnings?: string[];
} {
  // Memoize the expensive test bypass detection to avoid calling it on every render
  // Test bypass state doesn't change during component lifecycle, so we only check once
  const result = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        isActive: false,
        user: null,
        organizationId: null,
        source: undefined,
        securityWarnings: [],
      };
    }
    return detectClientSideTestBypass();
  }, []); // Empty deps - test bypass doesn't change during component lifecycle

  return {
    isActive: result.isActive,
    user: result.user ?? null,
    organizationId: result.organizationId ?? null,
    source: result.source,
    warnings: result.securityWarnings,
  };
}

/**
 * Get test user by role
 */
export function getTestUser(
  role: keyof typeof DEFAULT_TEST_USERS = "owner",
): TestUser {
  const user = DEFAULT_TEST_USERS[role];
  if (!user) {
    throw new Error(`Test user with role ${role} not found`);
  }
  return user;
}

/**
 * Check if current environment allows test bypass
 */
export function isTestBypassAllowed(): boolean {
  const config = getTestBypassConfig();
  return config.enabled;
}
